// src/admin/pages/Messages.tsx

import React, { useState, useEffect } from 'react';
import { AdminLayout } from '../components/AdminLayout';
import { Search, Filter, Mail, Clock, Check, ExternalLink } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { adminApi } from '../../utils/api';
import { Message } from '../../types';

export const Messages = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [messages, setMessages] = useState<Message[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [limit] = useState(10); // You can make this dynamic if needed
  const [loading, setLoading] = useState(false);

  const fetchMessages = async () => {
    setLoading(true);
    try {
      const response = await adminApi.messages.getAll();
      setMessages(response.data);
      setTotal(response.data.length);
    } catch (error: any) {
      console.error('Error fetching messages:', error);
      toast.error(error.message || 'Failed to fetch messages.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchMessages();
  }, [searchQuery, statusFilter, page]);

  const handleMarkAsRead = async (messageId: number) => {
    try {
      await adminApi.messages.markAsRead(messageId.toString());
      toast.success('Message marked as read');
      fetchMessages();
    } catch (error: any) {
      console.error('Error marking as read:', error);
      toast.error(error.message || 'Failed to mark as read.');
    }
  };

  const handleReply = async (message: Message) => {
    try {
      window.location.href = `mailto:${message.email}?subject=Re: ${encodeURIComponent(message.topic || 'Your Message')}&body=Hi ${encodeURIComponent(message.name)},%0D%0A%0D%0AThank you for reaching out!%0D%0A%0D%0ABest regards,%0D%0A[Your Name]`;

      await adminApi.messages.updateStatus(message.id.toString(), 'replied');
      toast.success('Message marked as replied');
      fetchMessages();
    } catch (error: any) {
      console.error('Error replying to message:', error);
      toast.error(error.message || 'Failed to reply to message.');
    }
  };

  const getStatusColor = (status: string) => {
    switch (status.toLowerCase()) {
      case 'unread':
        return 'bg-yellow-100 text-yellow-600';
      case 'read':
        return 'bg-blue-100 text-blue-600';
      case 'replied':
        return 'bg-green-100 text-green-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <AdminLayout>
      <div className="h-screen flex flex-col">
        <div className="flex-none p-6 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-2xl font-semibold text-gray-800">Contact Messages</h2>
            <div className="flex items-center gap-4">
              <div className="text-sm text-gray-500">
                Unread Messages: <span className="font-semibold text-pink-500">{messages.filter(m => m.read === 0).length}</span>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="relative">
              <input
                type="text"
                placeholder="Search messages..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-pink-500"
              />
              <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400" />
            </div>

            <div className="flex items-center gap-2 bg-white p-2 rounded-lg border">
              <Filter className="h-5 w-5 text-gray-400" />
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="flex-1 border-none focus:ring-0"
              >
                <option value="all">All Messages</option>
                <option value="unread">Unread</option>
                <option value="read">Read</option>
                <option value="replied">Replied</option>
              </select>
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-hidden">
          <div className="h-full overflow-auto px-6 pb-6">
            {loading ? (
              <div className="flex justify-center items-center py-10">
                <svg
                  className="animate-spin h-8 w-8 text-pink-500"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8v8H4z"
                  ></path>
                </svg>
              </div>
            ) : messages.length === 0 ? (
              <div className="flex justify-center items-center py-10">
                <p className="text-gray-500">No messages found.</p>
              </div>
            ) : (
              <div className="space-y-4">
                {messages.map((message) => (
                  <div
                    key={message.id}
                    className={`bg-white rounded-lg shadow-sm p-6 ${
                      message.read === 0 ? 'border-l-4 border-pink-500' : ''
                    }`}
                  >
                    <div className="flex justify-between items-start mb-4">
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">{message.name}</h3>
                        <div className="flex items-center gap-4 text-sm text-gray-500 mt-1">
                          <a
                            href={`mailto:${message.email}`}
                            className="flex items-center gap-1 hover:text-pink-500"
                          >
                            <Mail className="h-4 w-4" />
                            {message.email}
                          </a>
                          {message.phone && (
                            <a
                              href={`tel:${message.phone}`}
                              className="hover:text-pink-500"
                            >
                              {message.phone}
                            </a>
                          )}
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(message.status || 'new')}`}>
                          {(message.status || 'new').charAt(0).toUpperCase() + (message.status || 'new').slice(1)}
                        </span>
                        <span className="flex items-center gap-1 text-sm text-gray-500">
                          <Clock className="h-4 w-4" />
                          {new Date(message.inserted_at || message.created_at).toLocaleString()}
                        </span>
                      </div>
                    </div>

                    <p className="text-gray-600 mb-4">{message.message}</p>

                    <div className="flex justify-between items-center">
                      <div className="flex items-center gap-2">
                        {message.read === 0 && (
                          <button
                            onClick={() => handleMarkAsRead(message.id)}
                            className="flex items-center gap-1 px-3 py-1 bg-blue-50 text-blue-600 rounded-full text-sm hover:bg-blue-100"
                          >
                            <Check className="h-4 w-4" />
                            Mark as Read
                          </button>
                        )}
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => handleReply(message)}
                          className="flex items-center gap-1 px-3 py-1 bg-pink-50 text-pink-600 rounded-full text-sm hover:bg-pink-100"
                        >
                          <Mail className="h-4 w-4" />
                          Reply
                        </button>
                        {message.phone && (
                          <a
                            href={`https://wa.me/${message.phone.replace(/[^0-9]/g, '')}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-600 rounded-full text-sm hover:bg-green-100"
                          >
                            <ExternalLink className="h-4 w-4" />
                            WhatsApp
                          </a>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Pagination Controls */}
            {total > limit && (
              <div className="flex justify-center items-center mt-6 space-x-4">
                <button
                  onClick={() => setPage(prev => Math.max(prev - 1, 1))}
                  disabled={page === 1}
                  className={`px-4 py-2 rounded-lg ${
                    page === 1
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-pink-500 text-white hover:bg-pink-600'
                  }`}
                >
                  Previous
                </button>
                <span className="text-gray-700">
                  Page {page} of {totalPages}
                </span>
                <button
                  onClick={() => setPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={page === totalPages}
                  className={`px-4 py-2 rounded-lg ${
                    page === totalPages
                      ? 'bg-gray-200 text-gray-500 cursor-not-allowed'
                      : 'bg-pink-500 text-white hover:bg-pink-600'
                  }`}
                >
                  Next
                </button>
              </div>
            )}
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};
